import DateAndValue from "../DateAndValue";
import { DESCENDANT_ORDER, MONTHLY } from "../../config";

export default class AltmanZScore {
  constructor({
    ascendantDates = [],
    datesFormatted = [],
    yearsFormatted = [],
    monthlyCurrentAssets = [],
    monthlyTotalAssets = [],
    monthlyCurrentLiabilities = [],
    monthlyTotalLiabilities = [],
    monthlyBookValueOfEquity = [],
    monthlyRawRetainedEarnings = [],
    monthlyNetIncome = [],
    monthlyRevenue = [],
    monthlyEBIT = [],
    yearlyCurrentAssets = [],
    yearlyTotalAssets = [],
    yearlyCurrentLiabilities = [],
    yearlyTotalLiabilities = [],
    yearlyBookValueOfEquity = [],
    yearlyRawRetainedEarnings = [],
    yearlyNetIncome = [],
    yearlyRevenue = [],
    yearlyEBIT = [],
    dateOrder = DESCENDANT_ORDER,
    prefix = MONTHLY,
    businessType = "PRIVATE_MANUFACTURING",
  }) {
    this.ascendantDates = ascendantDates;
    this.datesFormatted = datesFormatted;
    this.yearsFormatted = yearsFormatted;
    this.monthlyCurrentAssets = monthlyCurrentAssets;
    this.monthlyTotalAssets = monthlyTotalAssets;
    this.monthlyCurrentLiabilities = monthlyCurrentLiabilities;
    this.monthlyTotalLiabilities = monthlyTotalLiabilities;
    this.monthlyBookValueOfEquity = monthlyBookValueOfEquity;
    this.monthlyRawRetainedEarnings = monthlyRawRetainedEarnings;
    this.monthlyNetIncome = monthlyNetIncome;
    this.monthlyRevenue = monthlyRevenue;
    this.monthlyEBIT = monthlyEBIT;
    this.yearlyCurrentAssets = yearlyCurrentAssets;
    this.yearlyTotalAssets = yearlyTotalAssets;
    this.yearlyCurrentLiabilities = yearlyCurrentLiabilities;
    this.yearlyTotalLiabilities = yearlyTotalLiabilities;
    this.yearlyBookValueOfEquity = yearlyBookValueOfEquity;
    this.yearlyRawRetainedEarnings = yearlyRawRetainedEarnings;
    this.yearlyNetIncome = yearlyNetIncome;
    this.yearlyRevenue = yearlyRevenue;
    this.yearlyEBIT = yearlyEBIT;
    this.dateOrder = dateOrder;
    this.prefix = prefix;
    this.businessType = businessType;
  }

  get constants() {
    const { businessType } = this;
    if(businessType === "NON_MANUFACTURING") return [6.560, 3.260, 6.720, 1.050, 0, 2.6, 1.1];
    else if(businessType === "PUBLIC_MANUFACTURING") return [1.200, 1.400, 3.300, 0.600, 0.999, 2.99, 1.8];
    else return [0.717, 0.847, 3.107, 0.420, 0.998, 2.9, 1.23];
  }

  get monthlyRetainedEarnings() {
    const { ascendantDates, monthlyRawRetainedEarnings, monthlyNetIncome } = this;
    let resultArray = [{ date: ascendantDates[0], value: 0 }];
    let value = 0;
    let len = ascendantDates.length;

    for (let i = 0; i < len; i++) {
      if(!i) value = 0;
      else if(i === 1) value = monthlyRawRetainedEarnings[i - 1].value + monthlyNetIncome[i - 1].value;
      else if(monthlyRawRetainedEarnings[i - 1].value === monthlyRawRetainedEarnings[i].value)
        value = resultArray[i - 1].value + monthlyNetIncome[i - 1].value;
      else value = monthlyRawRetainedEarnings[i].value;
      resultArray[i] = { date: ascendantDates[i], value };
    }
    return resultArray;
  }


  get monthlyWCTotalAssets() {
    const { monthlyCurrentAssets, monthlyCurrentLiabilities, monthlyTotalAssets } = this;
    return this.getArrayMultiply(
      this.getArraySum(monthlyCurrentAssets, monthlyCurrentLiabilities, { minus: true }),
      monthlyTotalAssets,
      { divide: true }
    );
  }

  get yearlyWCTotalAssets() {
    const { yearlyCurrentAssets, yearlyCurrentLiabilities, yearlyTotalAssets } = this;
    return this.getArrayMultiply(
      this.getArraySum(yearlyCurrentAssets, yearlyCurrentLiabilities, { minus: true, yearly: true }),
      yearlyTotalAssets,
      { divide: true, yearly: true }
    );
  }

  get monthlyRetainedEarningsTotalAssets() {
    const { monthlyRetainedEarnings, monthlyTotalAssets } = this;
    return this.getArrayMultiply(monthlyRetainedEarnings, monthlyTotalAssets, { divide: true });
  }

  get yearlyRetainedEarningsTotalAssets() {
    const { yearlyRawRetainedEarnings, yearlyTotalAssets } = this;
    return this.getArrayMultiply(yearlyRawRetainedEarnings, yearlyTotalAssets, { divide: true, yearly: true });
  }

  get monthlyEBITTotalAssets() {
    const { monthlyEBIT, monthlyTotalAssets } = this;
    return this.getValueLastTwelveMonth(monthlyEBIT, { array2: monthlyTotalAssets, oneDivider: true });
  }

  get yearlyEBITTotalAssets() {
    const { yearlyEBIT, yearlyTotalAssets } = this;
    return this.getArrayMultiply(yearlyEBIT, yearlyTotalAssets, { divide: true, yearly: true });
  }

  get monthlyBookValueofEquityTotalLiabilities() {
    const { monthlyBookValueOfEquity, monthlyTotalLiabilities } = this;
    return this.getArrayMultiply(monthlyBookValueOfEquity, monthlyTotalLiabilities, { divide: true });
  }

  get yearlyBookValueofEquityTotalLiabilities() {
    const { yearlyBookValueOfEquity, yearlyTotalLiabilities } = this;
    return this.getArrayMultiply(yearlyBookValueOfEquity, yearlyTotalLiabilities, { divide: true, yearly: true });
  }

  get monthlySalesTotalAssets() {
    const { monthlyRevenue, monthlyTotalAssets } = this;
    return this.getValueLastTwelveMonth(monthlyRevenue, { array2: monthlyTotalAssets, oneDivider: true });
  }

  get yearlySalesTotalAssets() {
    const { yearlyRevenue, yearlyTotalAssets } = this;
    return this.getArrayMultiply(yearlyRevenue, yearlyTotalAssets, { divide: true, yearly: true });
  }

  get monthlyZScore() {
    const {
      monthlyWCTotalAssets,
      monthlyRetainedEarningsTotalAssets,
      monthlyEBITTotalAssets,
      monthlyBookValueofEquityTotalLiabilities,
      monthlySalesTotalAssets,
      constants,
    } = this;
    return this.getMultipleSum(
      this.getArrayMultiply(monthlyWCTotalAssets, constants[0]),
      this.getArrayMultiply(monthlyRetainedEarningsTotalAssets, constants[1]),
      this.getArrayMultiply(monthlyEBITTotalAssets, constants[2]),
      this.getArrayMultiply(monthlyBookValueofEquityTotalLiabilities, constants[3]),
      this.getArrayMultiply(monthlySalesTotalAssets, constants[4])
    );
  }

  get yearlyZScore() {
    const {
      yearlyWCTotalAssets,
      yearlyRetainedEarningsTotalAssets,
      yearlyEBITTotalAssets,
      yearlyBookValueofEquityTotalLiabilities,
      yearlySalesTotalAssets,
      constants,
    } = this;
    return this.getMultipleSum(
      this.getArrayMultiply(yearlyWCTotalAssets, constants[0], { yearly: true }),
      this.getArrayMultiply(yearlyRetainedEarningsTotalAssets, constants[1], { yearly: true }),
      this.getArrayMultiply(yearlyEBITTotalAssets, constants[2], { yearly: true }),
      this.getArrayMultiply(yearlyBookValueofEquityTotalLiabilities, constants[3], { yearly: true }),
      this.getArrayMultiply(yearlySalesTotalAssets, constants[4], { yearly: true }),
      { yearly: true }
    );
  }

  get monthlyZone() {
    const { monthlyZScore, constants } = this;
    return monthlyZScore.map(
      (zScore) => ({
        date: zScore.date,
        value: zScore.value > constants[5] ? "Safe Zone" : (zScore.value > constants[6] ? "Grey Zone" : "Distress Zone"),
        color: zScore.value > constants[5] ? "text-green-500" : (zScore.value > constants[6] ? "text-gray-400" : "text-red-600"),
      })
    )
  }

  get yearlyZone() {
    const { yearlyZScore, constants } = this;
    return yearlyZScore.map(
      (zScore) => ({
        date: zScore.date,
        value: zScore.value > constants[5] ? "Safe Zone" : (zScore.value > constants[6] ? "Grey Zone" : "Distress Zone"),
        color: zScore.value > constants[5] ? "text-green-500" : (zScore.value > constants[6] ? "text-gray-400" : "text-red-600"),
      })
    )
  }

  getValueLastTwelveMonth(array1, options = { array2: [], average: false, oneDivider: false }) {
    //get average of last 12 months
    const TWELVE_MONTHS = 12;

    return this.ascendantDates.map((date, index) => {
      //LTM comes form earlier 12 months' value regardless dateorder
      let value = 0;
      let numerator = 0;
      let divider = 0;
      let disabled = true;

      if (index >= TWELVE_MONTHS - 1) {
        for (var i = 0; i < TWELVE_MONTHS; i++) numerator += array1[index - i].value;

        if(options.average) value = numerator / TWELVE_MONTHS;    //get average value of last 12 months
        else if(options.oneDivider) value = numerator / options.array2[index].value;    //divider is current value of array2
        else {                                                    //divider is also the sum of last 12 months of array2
          for (var i = 0; i < TWELVE_MONTHS; i++) divider += options.array2[index - i].value;
          value = numerator / divider;
        }

        disabled = false;
      }

      return new DateAndValue({
        date,
        value,
        disabled,
      });
    });
  }

  getArrayMultiply(array1, array2, options = { divide: false, yearly: false }) {
    //multiply element values of arrays

    let dateArray = this.ascendantDates;
    if(options.yearly) dateArray = this.yearsFormatted;

    return dateArray.map((date, index) => new DateAndValue({
        date,
        value: options.divide ?
          array1[index].value / array2[index].value :      //we divide arrays when option.divide is true
          array1[index].value * array2,                    //multiply is array * number
      })
    );
  }

  getArraySum(array1, array2, options = { yearly: false, minus: false }) {
    //add element values of arrays

    let dateArray = this.ascendantDates;
    if(options.yearly) dateArray = this.yearsFormatted;

    return dateArray.map((date, index) => new DateAndValue ({
        date,
        value: options.minus ?
          Number(array1[index].value) - Number(array2[index].value) :
          Number(array1[index].value) + Number(array2[index].value),
      })
    );
  }

  getMultipleSum(arr1, arr2, arr3, arr4, arr5, options = { yearly: false }) {
    //get a sum array of 5 array elements

    let dateArray = this.ascendantDates;
    if(options.yearly) dateArray = this.yearsFormatted;

    return dateArray.map((date, index) => new DateAndValue ({
        date,
        value: 
          Number(arr1[index].value) +
          Number(arr2[index].value) +
          Number(arr3[index].value) +
          Number(arr4[index].value) +
          Number(arr5[index].value),
      })
    );
  }
}
